<template>
  <BarChart :chartData="chartData" :options="options" />
</template>
<script lang="ts">
import type { PropType } from 'vue'
import { defineComponent, computed } from 'vue'
import { startOfMonth, endOfMonth, startOfDay } from 'date-fns'
import {
  Chart,
  BarElement,
  Tooltip,
  Legend,
  TimeScale,
  LinearScale,
  BarController,
} from 'chart.js'
import 'chartjs-adapter-date-fns'

import { BarChart } from 'vue-chart-3'
import { sum } from '@/libs/utilities/array'
import { createDatesOfMonth } from '@/libs/utilities/date'
import type { ChartData, ChartOptions } from 'chart.js'

Chart.register(
  BarController,
  BarElement,
  Tooltip,
  Legend,
  TimeScale,
  LinearScale
)

export type GraphData = {
  numberOfMessages: number
  date: Date
}

type DataMap = { [dayString: string]: GraphData['numberOfMessages'][] }

const createChartOptions: (
  min?: Maybe<string>,
  max?: Maybe<string>
) => ChartOptions = (min?: Maybe<string>, max?: Maybe<string>) => ({
  legend: {
    display: false,
  },
  tooltips: {
    backgroundColor: 'rgba(#030e1ab2, 0.7)',
    titleFontSize: 10,
    titleMarginBottom: 4,
    bodyFontStyle: 'bold',
    bodyFontSize: 10,
    xPadding: 8,
    yPadding: 6,
  },
  maintainAspectRatio: false,
  scales: {
    x: {
      type: 'time',
      stacked: true,
      time: {
        unit: 'day',
        tooltipFormat: 'yyyy年M月d日',
        displayFormats: {
          day: 'd',
        },
      },
      ticks: {
        min,
        max,
        fontColor: '#030e1a',
        fontSize: 14,
        padding: 0,
        lineHeight: 1,
      },
      gridLines: {
        display: false,
      },
    },
    y: {
      ticks: {
        precision: 0,
        fontColor: '#030e1a',
        fontSize: 14,
        lineHeight: 1.7,
        padding: 16,
      },
    },
  },
})

const toDateString = (date: Date): string => startOfDay(date).toISOString()

export default defineComponent({
  name: 'MatchChartRenderer',
  components: {
    BarChart,
  },
  props: {
    graphData: {
      type: Array as PropType<GraphData[]>,
      required: true,
    },
  },
  setup(props) {
    const dataMap = computed(
      (): DataMap =>
        props.graphData.reduce(
          (accumulator: DataMap, status: GraphData): DataMap => {
            const dateString = toDateString(status.date)
            const sameDateData = accumulator[dateString] || []
            return {
              ...accumulator,
              [dateString]: [...sameDateData, status.numberOfMessages],
            }
          },
          {}
        )
    )

    const options = computed((): ChartOptions => {
      if (props.graphData.length === 0) {
        return createChartOptions()
      }
      const [{ date: oneOfDates }, ...rest] = props.graphData
      const { start, end } = rest.reduce<{ start: Date; end: Date }>(
        (accumulator, { date }) => {
          accumulator.start =
            accumulator.start > date ? date : accumulator.start
          accumulator.end = accumulator.end > date ? accumulator.end : date
          return accumulator
        },
        { start: oneOfDates, end: oneOfDates }
      )

      return createChartOptions(
        startOfMonth(start).toISOString(),
        endOfMonth(end).toISOString()
      )
    })

    const chartData = computed((): ChartData<'bar'> => {
      type Accumulator = {
        matches: number[]
        messages: number[]
      }
      const labels =
        props.graphData.length > 0
          ? createDatesOfMonth(
              new Date(
                props.graphData[0].date.getFullYear(),
                props.graphData[0].date.getMonth(),
                props.graphData[0].date.getDate()
              )
            )
          : []

      // eslint-disable-next-line fp/no-mutating-methods
      const { matches, messages } = Object.keys(dataMap.value)
        .sort()
        .reduce<Accumulator>(
          ({ matches, messages }, dateString: string) => {
            const index = new Date(dateString).getDate() - 1
            matches[index] = dataMap.value[dateString].length
            messages[index] = sum(dataMap.value[dateString])
            return {
              matches: matches,
              messages: messages,
            }
          },
          {
            matches: Array.from({ length: labels.length }).map(() => 0),
            messages: Array.from({ length: labels.length }).map(() => 0),
          }
        )
      return {
        labels,
        datasets: [
          {
            label: '総マッチ数',
            data: matches,
            backgroundColor: '#006ee4',
            categoryPercentage: 0.4,
            barPercentage: 1.0,
          },
          {
            label: 'メッセージ数',
            data: messages,
            backgroundColor: 'rgba(11, 172, 211, 0.2)',
            categoryPercentage: 0.4,
            barPercentage: 1.0,
          },
        ],
      }
    })

    return {
      chartData,
      options,
    }
  },
})
</script>
